import { decamelizeKeys } from 'humps'
import { Room, RoomType, RoomUniqueId } from 'src/models/Room'
import { spliceArrayOne, spliceOne } from 'src/utils/array'
import { mutate as staticMutate } from 'swr'
import { SWRInfiniteResponse } from 'swr/infinite'
import { ApiClient } from '../client'
import {
  APPLY_COMPANY_MESSAGE_CUSTOM_STATUS,
  GET_ROOMS,
  POST_APPOINTMENT,
  postGroupDelete,
  postGroupRestore,
  postRoomHidden,
  putReverseRead,
} from '../endpoints'
import { PostAppointmentStatusRequest, RoomsResponse } from '../types/room'
import { toRoomsQuery } from './converters'

const findRoomIndex = (
  roomsArr: Room[][],
  roomUniqueId: string,
): [number, number] => {
  for (let i = 0; i < roomsArr.length; i++)
    for (let j = 0; j < roomsArr[i].length; j++)
      if (roomsArr[i][j].uniqueId === roomUniqueId) return [i, j]

  return [-1, -1]
}
const findRoomIndexByType = (
  roomsArr: Room[][],
  id: number,
  type: RoomType,
): [number, number] => {
  for (let i = 0; i < roomsArr.length; i++)
    for (let j = 0; j < roomsArr[i].length; j++)
      if (roomsArr[i][j].id === id && roomsArr[i][j].type === type)
        return [i, j]

  return [-1, -1]
}

const mutateRoom = (
  [roomsIdx, roomIdx]: [number, number],
  roomsArr: Room[][],
  changes: Partial<Room>,
) => {
  if (roomsIdx < 0 || roomIdx < 0) return roomsArr
  return spliceArrayOne(
    roomsArr,
    roomsIdx,
    spliceOne(roomsArr[roomsIdx], roomIdx, changes),
  )
}
const mutateCurrentRoom = (
  roomUniqueId: RoomUniqueId,
  userId: number,
  changes: Partial<Room>,
) =>
  staticMutate(
    [`${GET_ROOMS}${toRoomsQuery({ roomId: roomUniqueId, size: 1 })}`, userId],
    async (rooms: Room[] | undefined = []) => {
      const index = rooms.findIndex((each) => each.uniqueId === roomUniqueId)
      if (index < 0) return rooms
      return spliceOne(rooms, index, changes)
    },
    false,
  )

export const markAsRead = async (
  uniqueId: RoomUniqueId,
  userId: number,
  url: string,
  mutate: SWRInfiniteResponse<Room[], any>['mutate'],
) => {
  await fetch(url)
  const changes = { hasRead: true }
  await mutateCurrentRoom(uniqueId, userId, changes)
  return mutate(async (roomsArr: Room[][] | undefined = []) => {
    return mutateRoom(findRoomIndex(roomsArr, uniqueId), roomsArr, changes)
  }, false)
}
export const markAsUnread = async (
  id: number,
  roomType: RoomType,
  mutate?: SWRInfiniteResponse<Room[], any>['mutate'],
) => {
  const formData = new FormData()
  formData.append('message_type', roomType)
  const res = await ApiClient.put(putReverseRead(id), formData)
  if (res.error || !mutate) return res
  const changes = { hasRead: false }
  await mutate(async (roomsArr: Room[][] | undefined = []) => {
    return mutateRoom(
      findRoomIndexByType(roomsArr, id, roomType),
      roomsArr,
      changes,
    )
  }, false)
  return res
}

export const hideRoom = async (
  roomId: number,
  roomType: RoomType,
  url: string,
  roomUniqueId: RoomUniqueId,
  userId: number,
  mutate: SWRInfiniteResponse<Room[], any>['mutate'],
) => {
  const body = {
    company_message_room_hidden: decamelizeKeys({ roomType, roomId }),
  }
  const res = await ApiClient.post(postRoomHidden(url), body)
  if (res.error) return res
  await mutate(async (roomsArr: Room[][] | undefined = []) => {
    if (!res.data) return roomsArr
    const [roomsIdx, roomIdx] = findRoomIndexByType(roomsArr, roomId, roomType)
    if (roomsIdx < 0 || roomIdx < 0) return roomsArr
    const latest = await ApiClient.get<RoomsResponse>(
      `${GET_ROOMS}${toRoomsQuery({ roomId: roomUniqueId })}`,
    )
    if (latest.error || !latest.data?.rooms.length) return roomsArr
    const changes = {
      isHidden: true,
      unhiddenUrl: latest.data.rooms[0].unhiddenUrl,
    }
    if (roomUniqueId) await mutateCurrentRoom(roomUniqueId, userId, changes)
    return mutateRoom(
      findRoomIndexByType(roomsArr, roomId, roomType),
      roomsArr,
      changes,
    )
  }, false)
  return res
}

export const unhiddenRoom = async (
  url: string,
  roomUniqueId: RoomUniqueId,
  userId: number,
  mutate: SWRInfiniteResponse<Room[], any>['mutate'],
) => {
  const res = await ApiClient.delete(url)
  if (res.error) return res
  const changes = { isHidden: false, unhiddenUrl: null }
  await mutate(async (roomsArr: Room[][] | undefined = []) => {
    return mutateRoom(findRoomIndex(roomsArr, roomUniqueId), roomsArr, changes)
  }, false)
  await mutateCurrentRoom(roomUniqueId, userId, changes)
  return res
}

export const applyCustomStatus = async (
  roomId: number,
  roomType: RoomType,
  roomUniqueId: RoomUniqueId,
  statusId: number | null,
  userId: number,
  mutate: SWRInfiniteResponse<Room[], any>['mutate'],
) => {
  const res = await ApiClient.put(
    APPLY_COMPANY_MESSAGE_CUSTOM_STATUS,
    decamelizeKeys({ roomId, roomType, statusId }),
  )

  const changes = { customStatusId: statusId }
  await mutate(async (roomsArr: Room[][] | undefined = []) => {
    return mutateRoom(findRoomIndex(roomsArr, roomUniqueId), roomsArr, changes)
  }, false)
  await mutateCurrentRoom(roomUniqueId, userId, changes)
  return res
}

export const applyMoveBoard = async (
  roomId: number,
  roomType: RoomType,
  roomUniqueId: RoomUniqueId,
  statusId: number | null,
  hiddenUrl: string | null,
  unhiddenUrl: string | null,
  mutate: SWRInfiniteResponse<Room[], any>['mutate'],
) => {
  let changes = {}

  await ApiClient.put(
    APPLY_COMPANY_MESSAGE_CUSTOM_STATUS,
    decamelizeKeys({ roomId, roomType, statusId }),
  )

  changes = { ...changes, customStatusId: statusId }

  if (hiddenUrl) {
    await ApiClient.post(postRoomHidden(hiddenUrl), {
      company_message_room_hidden: decamelizeKeys({ roomType, roomId }),
    })
    const latest = await ApiClient.get<RoomsResponse>(
      `${GET_ROOMS}${toRoomsQuery({ roomId: roomUniqueId })}`,
    )

    if (latest.data?.rooms.length)
      changes = {
        ...changes,
        isHidden: true,
        unhiddenUrl: latest.data.rooms[0].unhiddenUrl,
      }
  }

  if (unhiddenUrl) {
    await ApiClient.delete(unhiddenUrl)
    changes = { ...changes, isHidden: false, unhiddenUrl: null }
  }
  await mutate(async (roomsArr: Room[][] | undefined = []) => {
    return mutateRoom(findRoomIndex(roomsArr, roomUniqueId), roomsArr, changes)
  }, false)
}

export const postAppointmentStatus = async ({
  userId,
  ...params
}: Omit<PostAppointmentStatusRequest['appointment'], 'targetCompanyId'> & {
  userId: number
}) => {
  const body: PostAppointmentStatusRequest = {
    appointment: { ...params, targetCompanyId: userId },
  }
  return ApiClient.post(POST_APPOINTMENT, decamelizeKeys(body))
}

// Referrer only
export const deleteGroup = async ({ roomId }: { roomId: number }) => {
  const formData = new FormData()
  formData.append('status', 'trashed')
  formData.append('_method', 'PUT')
  return ApiClient.post(postGroupDelete(roomId), formData)
}
export const restoreDeletedGroup = async ({ roomId }: { roomId: number }) => {
  const formData = new FormData()
  formData.append('status', 'available')
  formData.append('_method', 'PUT')
  return ApiClient.post(postGroupRestore(roomId), formData)
}
